import { useState } from "react";
import { X } from "lucide-react";
import { useApiUserApplicationsCommentsCreate } from "@/api/lctApIComponents";
import { useAppSelector } from "@/hooks/reduxHooks";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { UserApplicationComment } from "@/api/lctApISchemas";
import { useSections } from "@/hooks/useSections";
import { FeedbackItem } from "./FeedbackItem";
import { CarbonScore } from "./CarbonScore";
import { useFeedback } from "@/hooks/useFeedback";
import { CommentType } from "@/utils/constants";
import { useUserAppInfo } from "@/hooks/useUserAppInfo";
import ApplicationReferenceButton from "./ApplicationReferenceButton";
import { AutoCarbonScore } from "@/components/AutoCarbonScore";

interface IProps {
  pvpId: string;
  appId: string;
  userAppId: string;
  toggleFeedback: () => void;
}

const GENERAL_SECTION = "general";

export const FeedbackSection: React.FC<IProps> = ({ appId, pvpId, userAppId, toggleFeedback }) => {
  const token = useAppSelector((state) => state.user.authKey);
  const isStaff = useAppSelector((state) => state.user.isStaff);
  const [feedbackText, setFeedbackText] = useState<string>("");
  const [selectedSection, setSelectedSection] = useState<string>(GENERAL_SECTION);
  const addFeedback = useApiUserApplicationsCommentsCreate();
  const { feedbackItemsLength, feedbackLocked, carbonScoreComment, feedbackItems, carbonScore, refetchFeedbackItems } =
    useFeedback(userAppId);
  const { userApplicationId, showCarbonScore } = useUserAppInfo(userAppId);
  const { sections, getSectionTitle } = useSections(appId, pvpId);

  const renderSectionList = () => {
    return (
      <Select value={selectedSection} onValueChange={(value: string) => setSelectedSection(value)}>
        <SelectTrigger>
          <SelectValue placeholder="Section for feedback" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem key={"general"} value={"general"}>
            General
          </SelectItem>
          {sections?.results?.map((section) => (
            <SelectItem key={`section-${section.id}`} value={section.id}>
              {`${section.header_text}`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  const onAddFeedback = () => {
    const body: Omit<UserApplicationComment, "id" | "created_at" | "updated_at" | "user_full_name" | "edited_by_name"> = {
      comment_text: feedbackText,
      associated_section: selectedSection,
      comment_type: CommentType.FEEDBACK,
    };
    if (selectedSection === GENERAL_SECTION) {
      delete body.associated_section;
    }
    addFeedback.mutate(
      {
        headers: { Authorization: `Token ${token}` },
        pathParams: { userapp: userAppId },
        body: body as UserApplicationComment,
      },
      {
        onSuccess: () => {
          refetchFeedbackItems();
          setSelectedSection(GENERAL_SECTION);
          setFeedbackText("");
        },
      },
    );
  };

  return (
    <aside className="no-scrollbar flex max-h-full min-h-0 min-w-10 flex-col overflow-y-auto rounded-lg bg-slate-200 p-4 md:w-[320px]">
      <h2 className="flex flex-row justify-between gap-2 text-lg font-semibold">
        <span>{`Feedback ${feedbackItemsLength > 0 ? "(" + feedbackItemsLength + ")" : ""}`}</span>
        <X className="" onClick={toggleFeedback} />
      </h2>
      {!feedbackLocked ? (
        <>
          <div className="my-2">{renderSectionList()}</div>
          <Textarea
            className="mb-2"
            placeholder="Enter feedback here"
            value={feedbackText}
            onChange={(event) => setFeedbackText(event.target.value)}
          />
          <Button
            className="self-end"
            onClick={onAddFeedback}
            loading={addFeedback.isPending}
            disabled={!feedbackText || addFeedback.isPending}
          >
            Submit
          </Button>
        </>
      ) : null}

      <div className="my-2 flex flex-col gap-4">
        {userApplicationId ? <ApplicationReferenceButton userApplicationId={userApplicationId} /> : null}
        {showCarbonScore && carbonScore !== undefined && carbonScore !== null ? (
          <CarbonScore userAppId={userAppId} comment={carbonScoreComment} />
        ) : isStaff ? (
          <AutoCarbonScore userAppId={userAppId} />
        ) : null}
        {feedbackItems?.map((comment) =>
          comment.comment_type === CommentType.FEEDBACK ? (
            <FeedbackItem
              key={comment.id}
              comment={comment}
              sectionTitle={getSectionTitle(comment.associated_section)}
              userAppId={userAppId}
              refetch={refetchFeedbackItems}
              editable={!feedbackLocked}
            />
          ) : null,
        )}
        {feedbackItemsLength === 0 ? <div className="text-center">No feedback yet</div> : null}
      </div>
    </aside>
  );
};
